import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

const MAX_QUERY_RETRIES = 3;

type ErrorAlertMeta = {
  /** Translation key of the error alert shown when the query or mutation fails */
  errorMessageKey?: string;
};

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: ErrorAlertMeta;
    mutationMeta: ErrorAlertMeta;
  }
}

type ErrorAlertHandler = (errorMessageKey: string) => void;

let errorAlertHandler: ErrorAlertHandler | undefined;

/**
 * Registers the function that shows `meta.errorMessageKey` alerts. Returns an unregister function.
 * @param handler
 */
export const setErrorAlertHandler = (handler: ErrorAlertHandler) => {
  errorAlertHandler = handler;
  return () => {
    if (errorAlertHandler === handler) {
      errorAlertHandler = undefined;
    }
  };
};

/**
 * Shows the error alert of a failed query or mutation that declares `meta.errorMessageKey`.
 * @param meta
 */
const showErrorAlert = (meta: ErrorAlertMeta | undefined) => {
  if (meta?.errorMessageKey) {
    errorAlertHandler?.(meta.errorMessageKey);
  }
};

/**
 * Retry network errors and 5xx responses (including the 503 a worker returns while it is still
 * loading Entra sign-in keys), but not 4xx responses, which won't change on retry.
 * failureCount starts at 0, so this keeps TanStack's default of 3 retries.
 */
export const shouldRetryQuery = (
  failureCount: number,
  error: unknown,
): boolean => {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status !== undefined && status < 500) {
    return false;
  }
  return failureCount < MAX_QUERY_RETRIES;
};

/**
 * How long reference lists stay fresh. They change rarely, every change through the app invalidates
 * them, and grids read them once per cell, so they shouldn't refetch on every mount.
 */
export const REFERENCE_DATA_STALE_TIME = 5 * 60 * 1000;

export const createQueryClient = () =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (_error, query) => showErrorAlert(query.meta),
    }),
    mutationCache: new MutationCache({
      onError: (_error, _variables, _context, mutation) =>
        showErrorAlert(mutation.meta),
    }),
    defaultOptions: {
      queries: {
        retry: shouldRetryQuery,
      },
    },
  });

export const queryClient = createQueryClient();
